/**
 * SNSPreviewManagerHandler - Main Controller Class
 * Manages SNS URL detection, hiding, and preview generation
 * Converted from sns-preview.js function-based implementation
 */

import { SNSDetector } from "../core/sns-detector";
import { SNSUrlHider } from "../core/sns-url-hider";
import { TaskScheduler } from "../../core/task-scheduler";
import { OgpRender } from "../../util/ogp/ogp-render";
import { Patterns } from "../../constants/patterns";
import { Placeholders } from "../../constants/placeholders";
import { SNSLinksUtils } from "../links/sns-links-utils";
import { Roles } from "../../constants/roles";
import { WysiwygUtils } from "../../wysiwyg/wysiwyg-utils";
import { SnsCardPreviewPreviewsRender } from "./render/card-preview/sns-card-preview-previews-render";
import { SnsEmbedRender } from "./render/embed/sns-embed-render";
import { SnsCardPreviewFirstTileRender } from "./render/card-preview/sns-card-preview-first-tile-render";
import { SnsCardPreviewTilesGridRender } from "./render/card-preview/sns-card-preview-tiles-grid-render";
import { SnsCardPreviewIconsRender } from "./render/card-preview/sns-card-preview-icons-render";
import { SnsCardPreviewMediaGridRender } from "./render/card-preview/sns-card-preview-media-grid-render";
import { SnsCardPreviewPlaceholderRender } from "./render/card-preview/sns-card-preview-placeholder-render";
import { snsPreviewManagerForm } from "./sns-preview-manager-form";
import { SnsPreviewManagerListener } from "./sns-preview-manager-listener";
import type { DetectedLinksMap } from "../core/sns-detector";

type SnsPreviewManagerOptions = Record<string, unknown> & {
    enableLinkHiding?: boolean;
    enableIcons?: boolean;
    enableEmbeds?: boolean;
};

class SNSPreviewManagerHandler {
    private static readonly displayContentSelector = [
        `[data-role="${Roles.POST_CONTENT_DISPLAY}"]`,
        `[data-role="${Roles.POST_DETAIL_CONTENT}"]`,
    ].join(", ");

    private static readonly snsLinksDataSelector = [`[data-role="${Roles.POST_SNS_LINKS_DATA}"]`].join(", ");

    private static readonly boardMediaAreaSelector = `[data-role="${Roles.BOARD_MEDIA_AREA}"]`;
    private static readonly boardImagesGridSelector = `[data-role="${Roles.BOARD_IMAGES_GRID}"]`;
    private static readonly boardVideoWrapSelector = `[data-role="${Roles.BOARD_VIDEO_WRAP}"]`;
    private static readonly snsContentSelector = `[data-role="${Roles.SNS_CONTENT}"]`;
    private static readonly postTitleSelector = `[data-role="${Roles.POST_CARD_TITLE}"], [data-role="${Roles.POST_DETAIL_TITLE}"]`;

    private options: SnsPreviewManagerOptions;
    private detector: SNSDetector;
    private urlHider: SNSUrlHider;
    private ogpRenderer: OgpRender;
    private isInitialized: boolean;
    private processedElements: WeakSet<Element>;

    /** @returns {string[]} */
    private createStringList(): string[] {
        return [];
    }

    /** @returns {Set<string>} */
    private createStringSet(): Set<string> {
        return new Set();
    }

    public constructor(options: SnsPreviewManagerOptions = {}) {
        this.options = {
            ...Patterns.SNS_DEFAULT_OPTIONS,
            ...options,
        };

        // Initialize components
        this.detector = new SNSDetector();
        this.urlHider = new SNSUrlHider();
        this.ogpRenderer = new OgpRender();

        // State management
        this.isInitialized = false;
        this.processedElements = new WeakSet();

        this.initialize();
    }

    private initialize(): void {
        if (this.isInitialized) return;

        // Check page context and initialize appropriately
        const isCreationPage = snsPreviewManagerForm.hasPostCreateForm();
        if (isCreationPage) {
            this.initializeForCreationPages();
        }

        this.isInitialized = true;
    }

    public initializeForCreationPages(): void {
        // Setup for Quill editor content change detection
        this.setupQuillDetection();

        // Setup for regular textarea content change detection
        this.setupTextareaDetection();
    }

    private setupQuillDetection(): void {
        const quillContainer = snsPreviewManagerForm.getWysiwygEditor();
        if (!quillContainer) {
            return;
        }

        // Use MutationObserver to detect changes
        const observer = new MutationObserver(() => {
            TaskScheduler.scheduleDebounced(
                () => {
                    const quillContent = snsPreviewManagerForm.getWysiwygContent(quillContainer);
                    if (quillContent) {
                        this.processContentForCreation(quillContent);
                    }
                },
                50,
                "quill-content-detection",
            );
        });

        observer.observe(quillContainer, {
            childList: true,
            subtree: true,
            characterData: true,
        });

        // Initial processing
        const quillContent = snsPreviewManagerForm.getWysiwygContent(quillContainer);
        if (quillContent) {
            this.processContentForCreation(quillContent);
        }
    }

    private setupTextareaDetection(): void {
        const textarea = snsPreviewManagerForm.getPostContent();
        if (!textarea) {
            return;
        }

        SnsPreviewManagerListener.bindTextareaInput(textarea, () => {
            TaskScheduler.scheduleDebounced(
                () => {
                    this.processContentForCreation(textarea);
                },
                50,
                "textarea-content-detection",
            );
        });
    }

    public processContentForCreation(element: Element): void {
        if (this.processedElements.has(element)) {
            return; // Skip if recently processed
        }

        const content = this.getElementContent(element);
        if (!content) {
            return;
        }

        // Detect SNS links
        const detectedLinks = this.detector.detectLinks(content);

        // IMPORTANT (create/edit form UX):
        // - We hide URLs from the editor content to keep it clean.
        // - But if we hide first, the URL never reaches the sns_links manager.
        // So we sync into sns_links *before* hiding.
        try {
            const urls = this.createStringList();
            Object.keys(detectedLinks || {}).forEach((platform) => {
                const items = detectedLinks[platform];
                if (!Array.isArray(items)) return;
                items.forEach((item) => {
                    const u = typeof item === "string" ? item : item && typeof item === "object" ? item.url : null;
                    if (typeof u === "string" && u.trim()) urls.push(u);
                });
            });

            const normalizedUrls = SNSLinksUtils.normalizeSnsLinks(urls);
            if (normalizedUrls.length > 0) {
                const textarea =
                    element instanceof HTMLTextAreaElement
                        ? element
                        : snsPreviewManagerForm.getPostContentTextarea(element);

                if (textarea instanceof HTMLTextAreaElement) {
                    // Avoid rewriting the list if nothing new.
                    const list = textarea.form ? snsPreviewManagerForm.getSnsLinksList(textarea.form) : null;
                    const current = list
                        ? snsPreviewManagerForm
                              .getSnsLinkInputs(list)
                              .map((el) => (el instanceof HTMLInputElement ? el.value.trim() : ""))
                              .filter(Boolean)
                        : [];

                    const currentNormalized = SNSLinksUtils.normalizeSnsLinks(current);
                    const merged = SNSLinksUtils.normalizeSnsLinks([...currentNormalized, ...normalizedUrls]);
                    if (merged.join("\n") !== currentNormalized.join("\n")) {
                        WysiwygUtils.mergeSnsLinksIntoEditableList(textarea, normalizedUrls);
                    }
                }
            }
        } catch (_e) {
            // Ignore sync failures; URL hiding and other processing still proceed.
        }

        // Apply link hiding if enabled
        if (this.options.enableLinkHiding && Object.keys(detectedLinks).length > 0) {
            this.urlHider.hideUrlsFromElement(element, detectedLinks);
        }

        // Mark as processed temporarily
        this.processedElements.add(element);
        // Keep setTimeout for cache expiration - intentional timing behavior
        setTimeout(() => {
            this.processedElements.delete(element);
        }, 500); // Allow reprocessing after 0.5 second
    }

    private getElementContent(element: Element | HTMLInputElement | HTMLTextAreaElement | null | undefined): string {
        if (!element) return "";

        if ("innerHTML" in element && typeof element.innerHTML === "string") {
            return element.innerHTML;
        } else if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
            return element.value;
        } else if (element.textContent !== undefined) {
            return element.textContent || "";
        }

        return "";
    }

    /**
     * Extract previewable image URLs from HTML content.
     * @param {string} html
     * @returns {string[]}
     */
    private extractImageUrlsFromContent(html: string): string[] {
        const source = String(html || "").trim();
        if (!source) return [];

        const doc = new DOMParser().parseFromString(source, "text/html");
        const imageEls = snsPreviewManagerForm.getImageElements(doc);
        if (!imageEls.length) return [];

        const unique = this.createStringSet();

        imageEls.forEach((img) => {
            if (!(img instanceof HTMLImageElement)) return;

            const raw =
                String(img.getAttribute("src") || "").trim() ||
                String(img.getAttribute("data-src") || "").trim() ||
                String(img.getAttribute("data-original") || "").trim();
            if (!raw) return;

            const decoded = Patterns.decodeAmpersand(raw).trim();
            if (!decoded) return;
            if (/^javascript:/i.test(decoded)) return;

            unique.add(decoded);
        });

        return Array.from(unique);
    }

    /**
     * @returns {string}
     */
    private buildNoImagePlaceholderInnerHtml(): string {
        return SnsCardPreviewPlaceholderRender.buildNoImagePlaceholderInnerHtml();
    }

    /**
     * @param {HTMLElement} mediaArea
     */
    private ensureNoImagePlaceholderInMediaArea(mediaArea: HTMLElement): void {
        SnsCardPreviewPlaceholderRender.ensureNoImagePlaceholderInMediaArea(mediaArea);
    }

    private getCardMediaArea(root: ParentNode): HTMLElement | null {
        return snsPreviewManagerForm.getBoardMediaArea(root);
    }

    /**
     * @param {HTMLImageElement} img
     */
    private handleCardImageLoadError(img: HTMLImageElement): void {
        if (!(img instanceof HTMLImageElement)) return;
        if (img.dataset.cardImageFallbackDone === "1") return;
        img.dataset.cardImageFallbackDone = "1";

        const tile = snsPreviewManagerForm.getClosestPreviewTile(img);
        if (tile instanceof HTMLElement) {
            SnsCardPreviewPlaceholderRender.renderNoImagePlaceholderTile(tile);
            return;
        }

        const mediaArea = snsPreviewManagerForm.getClosestBoardMediaArea(img);
        const imageGrid = snsPreviewManagerForm.getClosestBoardImagesGrid(img);

        img.remove();

        if (!(mediaArea instanceof HTMLElement)) return;
        if (!(imageGrid instanceof HTMLElement)) {
            this.ensureNoImagePlaceholderInMediaArea(mediaArea);
            return;
        }

        const remaining = snsPreviewManagerForm.getBoardImages(imageGrid).length;
        if (remaining > 0) return;

        imageGrid.remove();
        this.ensureNoImagePlaceholderInMediaArea(mediaArea);
    }

    /**
     * @param {HTMLImageElement} img
     */
    private registerCardImageFallback(img: HTMLImageElement): void {
        if (!(img instanceof HTMLImageElement)) return;

        SnsPreviewManagerListener.bindCardImageError(img, () => {
            this.handleCardImageLoadError(img);
        });

        // Already completed as broken before listener registration.
        if (img.complete && img.naturalWidth === 0) {
            this.handleCardImageLoadError(img);
        }
    }

    // === PUBLIC API METHODS ===

    /**
     * Initialize for index pages (card view) - Legacy compatible
     * Note: This function performs URL/tag cleaning AND preview generation for cards
     */
    public initForIndex(postSelector = `[data-role="${Roles.POST_CARD}"]`, createTiles = true): void {
        const postElements = snsPreviewManagerForm.getPostElements(document, postSelector);

        if (!postElements.length) {
            return;
        }

        postElements.forEach((postElement) => {
            // Skip if already processed
            if (postElement.hasAttribute("data-sns-processed")) {
                return;
            }

            // Look for content
            const hiddenContentEl = snsPreviewManagerForm.getHiddenContent(postElement);
            const visibleContentEl = snsPreviewManagerForm.getDisplayContent(postElement);
            const snsContentEl = snsPreviewManagerForm.getSnsContent(postElement);
            const snsLinksDataEls = snsPreviewManagerForm.getSnsLinksDataElements(postElement);

            let content = "";
            if (hiddenContentEl) {
                const hiddenInnerText = hiddenContentEl instanceof HTMLElement ? hiddenContentEl.innerText : "";
                content = hiddenContentEl.innerHTML || hiddenContentEl.textContent || hiddenInnerText || "";
            } else if (visibleContentEl) {
                const visibleInnerText = visibleContentEl instanceof HTMLElement ? visibleContentEl.innerText : "";
                content = visibleContentEl.innerHTML || visibleContentEl.textContent || visibleInnerText || "";
            }

            // Check for SNS iframe content (Facebook, etc.)
            if (snsContentEl) {
                const snsContent = snsContentEl.innerHTML || "";
                if (snsContent.trim()) {
                    content += " " + snsContent;
                }
            }

            // Detect SNS links from both content and sns_links field
            let detectedLinks: DetectedLinksMap = {};
            let normalizedSnsLinks = this.createStringList();

            // First, detect from content HTML
            if (content.trim()) {
                detectedLinks = this.detector.detectLinks(content);
            }

            // Second, add sns_links from all metadata blocks if available
            if (snsLinksDataEls.length > 0) {
                const collectedSnsLinks = this.createStringList();

                snsLinksDataEls.forEach((snsLinksDataEl) => {
                    try {
                        const snsLinksJson = snsLinksDataEl.getAttribute("data-sns-links");
                        if (!snsLinksJson) return;
                        const normalizedLinks = SNSLinksUtils.parseSnsLinksDataAttribute(snsLinksJson);
                        if (normalizedLinks.length > 0) {
                            collectedSnsLinks.push(...normalizedLinks);
                        }
                    } catch (_e) {
                        // Ignore malformed legacy fragments and continue with other blocks.
                    }
                });

                normalizedSnsLinks = SNSLinksUtils.normalizeSnsLinks(collectedSnsLinks);
                if (normalizedSnsLinks.length > 0) {
                    // Merge sns_links into detectedLinks
                    normalizedSnsLinks.forEach((urlString) => {
                        const decodedLink = Patterns.decodeAmpersand(urlString);
                        const platform = this.detector.detectPlatform(decodedLink);
                        if (platform && platform !== "unknown") {
                            if (!detectedLinks[platform]) {
                                detectedLinks[platform] = [];
                            }
                            const linkObj = {
                                fullMatch: decodedLink,
                                id: null,
                                url: decodedLink,
                                index: 0,
                            };
                            const exists = detectedLinks[platform].some(
                                (item) => (typeof item === "string" ? item : item.url) === decodedLink,
                            );
                            if (!exists) {
                                detectedLinks[platform].push(linkObj);
                            }
                        }
                    });
                }
            }

            const hasSNSLinks = Object.keys(detectedLinks).length > 0;

            const hasContent = Boolean(content.trim());

            // If we have multiple kinds of media (images/videos/OGP/SNS),
            // compose them into the same 80x80 placeholder area as tiles.
            const mediaArea = this.getCardMediaArea(postElement);
            if (mediaArea instanceof HTMLElement) {
                const hasAttachedMedia = snsPreviewManagerForm.hasAttachedCardMedia(mediaArea);

                if (!hasAttachedMedia) {
                    const contentImageUrls = this.extractImageUrlsFromContent(content).slice(0, 4);

                    if (contentImageUrls.length > 0) {
                        const placeholder = snsPreviewManagerForm.getBoardMediaPlaceholder(mediaArea);
                        if (placeholder) placeholder.remove();
                        const renderedImages = SnsCardPreviewMediaGridRender.renderBoardImagesGrid(
                            mediaArea,
                            contentImageUrls,
                        );
                        renderedImages.forEach((img) => this.registerCardImageFallback(img));
                    }
                }

                const imageEls = snsPreviewManagerForm.getBoardImagesInMediaArea(mediaArea);
                const videoWrap = snsPreviewManagerForm.getBoardVideoWrap(mediaArea);

                const ogpUrls = hasContent ? this.ogpRenderer.detectUrls(content) : [];
                const snsCount =
                    Array.isArray(normalizedSnsLinks) && normalizedSnsLinks.length ? normalizedSnsLinks.length : 0;
                const hasImages = imageEls.length > 0;
                const hasVideo = videoWrap instanceof HTMLElement;
                const ogpCount = ogpUrls && ogpUrls.length ? 1 : 0;
                const hasOgp = ogpCount === 1;
                const hasSns = hasSNSLinks;

                const existingCount = Math.min(imageEls.length, 4) + (hasVideo ? 1 : 0);
                const totalCount = existingCount + ogpCount + (hasSns ? Math.min(Math.max(snsCount, 1), 4) : 0);
                const mediaTypesCount = (hasImages ? 1 : 0) + (hasVideo ? 1 : 0) + (hasOgp ? 1 : 0) + (hasSns ? 1 : 0);

                // Keep legacy layout when only images or only video exist.
                const shouldUseTilesGrid = createTiles && totalCount >= 2 && mediaTypesCount >= 2;
                if (shouldUseTilesGrid) {
                    const placeholder = snsPreviewManagerForm.getBoardMediaPlaceholder(mediaArea);
                    if (placeholder) placeholder.remove();

                    let grid = snsPreviewManagerForm.getSnsPreviewTilesGrid(mediaArea);
                    if (!(grid instanceof HTMLElement)) {
                        grid = SnsCardPreviewMediaGridRender.ensureIndexTilesGrid(mediaArea);
                    }
                    if (!(grid instanceof HTMLElement)) return;

                    // Move image thumbnails into tiles (up to 4)
                    if (imageEls.length) {
                        const srcs = imageEls
                            .map((img) => String(img.getAttribute("src") || "").trim())
                            .filter(Boolean)
                            .slice(0, 4);

                        const imageGrid = snsPreviewManagerForm.queryScopedElement(
                            mediaArea,
                            SNSPreviewManagerHandler.boardImagesGridSelector,
                        );
                        if (imageGrid instanceof HTMLElement) {
                            imageGrid.remove();
                        }

                        srcs.forEach((src) => {
                            if (grid.children.length >= 4) return;
                            const img = SnsCardPreviewMediaGridRender.appendImageTileToIndexGrid(grid, src);
                            if (img) this.registerCardImageFallback(img);
                        });
                    }

                    // Move video preview into a tile
                    if (videoWrap instanceof HTMLElement) {
                        if (grid.children.length < 4) {
                            SnsCardPreviewMediaGridRender.appendVideoWrapTileToIndexGrid(grid, videoWrap);
                        } else {
                            videoWrap.remove();
                        }
                    }

                    grid.setAttribute("data-count", String(grid.children.length));
                }
            }

            // CSR OGP fetch disabled: SSR renders ogp-preview-card from attached_images (ogp_* files)
            // if (hasContent) {
            //     this.processOgpForCard(postElement, content);
            // }
            if (!hasSNSLinks) {
                // No SNS links and OGP CSR disabled - show TEXT ONLY immediately
                this.updatePlaceholderText(postElement, Placeholders.TEXT_ONLY);
            }

            if (hasSNSLinks) {
                // Hide URLs from visible content
                if (visibleContentEl) {
                    this.urlHider.hideUrlsFromElement(visibleContentEl, detectedLinks);
                }

                // Also clean up title if it contains meaningless content
                const titleElement = snsPreviewManagerForm.getPostTitle(postElement);
                if (titleElement) {
                    this.urlHider.hideUrlsFromElement(titleElement, detectedLinks);
                }

                // Skip SNS tile rendering if SSR already rendered an OGP preview card from attached_images,
                // or if tile creation was disabled for this context (e.g. detail/modal view).
                const hasExistingOgpPreview =
                    mediaArea instanceof HTMLElement && snsPreviewManagerForm.getOgpPreviewCard(mediaArea);

                if (createTiles && !hasExistingOgpPreview) {
                    // Index/list cards: render SNS as tiles only (no legacy platform iframes/thumbnails)
                    // to keep YouTube behavior consistent across cards.
                    const renderedGrid = SnsCardPreviewTilesGridRender.addSnsTilesGridPreviewToCard(
                        postElement,
                        normalizedSnsLinks,
                        detectedLinks,
                    );
                    if (!renderedGrid) {
                        // If placeholder is still present (e.g. single SNS link), render the first SNS link as a tile.
                        SnsCardPreviewFirstTileRender.addFirstSnsTilePreviewToCard(
                            postElement,
                            normalizedSnsLinks,
                            detectedLinks,
                        );
                    }
                }

                // Add icons
                this.addSNSIconsToCard(postElement, detectedLinks);

                postElement.setAttribute("data-sns-processed", "true");
            } else {
                // Even without SNS links, clean up meaningless content
                if (visibleContentEl) {
                    const titleElement = snsPreviewManagerForm.getPostTitle(postElement);
                    if (titleElement) {
                        this.urlHider.hideUrlsFromElement(titleElement, {});
                    }
                    this.urlHider.hideUrlsFromElement(visibleContentEl, {});
                }
            }
        });
    }

    /**
     * Initialize for display pages (single post view)
     */
    public initForDisplay(
        contentSelector = `[data-role="${Roles.POST_DETAIL_CONTENT}"]`,
        lightweightMode = false,
    ): void {
        const contentElements = snsPreviewManagerForm.getContentElements(document, contentSelector);

        contentElements.forEach((element) => {
            this.processContentForDisplay(element, lightweightMode);
        });
    }

    /**
     * Process post for index page
     */
    private processPostForIndex(postElement: Element): void {
        const content = this.getPostContent(postElement);

        if (!content) {
            return;
        }

        const detectedLinks = this.detector.detectLinks(content);

        if (this.options.enableLinkHiding) {
            this.urlHider.hideUrlsFromElement(postElement, detectedLinks);
        }

        if (this.options.enableIcons) {
            this.addSNSIconsToCard(postElement, detectedLinks);
        }

        if (this.options.enableEmbeds) {
            SnsCardPreviewPreviewsRender.addPreviewsToCard({
                cardElement: postElement,
                detectedLinks,
                ogpRenderer: this.ogpRenderer,
            });
        }
    }

    /**
     * Process content for display page
     */
    public processContentForDisplay(contentElement: Element, lightweightMode = false): void {
        if (!(contentElement instanceof HTMLElement)) {
            return;
        }

        // Prevent duplicate processing
        if (contentElement.hasAttribute("data-sns-processed")) {
            return;
        }

        // Skip processing for card display content in index pages (only show in modals)
        // Index pages already have previews in .board-media-area
        const isInModal = snsPreviewManagerForm.isInsideModal(contentElement);
        const isPostContent = contentElement.matches?.(SNSPreviewManagerHandler.displayContentSelector) ?? false;
        const isInBoardCard = snsPreviewManagerForm.isInsidePostCard(contentElement);

        // Skip only if it's card display content in index.blade and not in modal
        if (isPostContent && isInBoardCard && !isInModal) {
            // Skip: card display content in index.blade should not show previews
            contentElement.setAttribute("data-sns-processed", "true");
            return;
        }

        // Clear existing SNS and OGP previews before re-processing
        this.clearExistingPreviews(contentElement);

        const content = this.getElementContent(contentElement);
        // Avoid double-detecting URLs embedded inside the hidden sns_links metadata block
        // (data-sns-links contains URLs and can be picked up by the generic URL detector)
        const contentClone = contentElement.cloneNode(true);
        if (contentClone instanceof HTMLElement) {
            snsPreviewManagerForm.getSnsLinksDataElements(contentClone).forEach((node) => node.remove());
        }
        const contentForDetection =
            contentClone instanceof HTMLElement ? this.getElementContent(contentClone) : content;
        const detectedLinks = this.detector.detectLinks(contentForDetection);
        const shouldDisableEmbeds = contentElement.getAttribute("data-disable-sns-embeds") === "1";

        // Check for sns_links data attribute (from show.blade.php / show-modal.blade.php)
        const snsLinksDataEls = snsPreviewManagerForm.getSnsLinksDataElements(contentElement);

        if (snsLinksDataEls.length > 0) {
            const collectedSnsLinks = this.createStringList();

            snsLinksDataEls.forEach((snsLinksDataEl) => {
                try {
                    const snsLinksJson = snsLinksDataEl.getAttribute("data-sns-links");
                    if (!snsLinksJson) return;
                    const normalizedLinks = SNSLinksUtils.parseSnsLinksDataAttribute(snsLinksJson);
                    if (normalizedLinks.length > 0) {
                        collectedSnsLinks.push(...normalizedLinks);
                    }
                } catch (_e) {
                    // Ignore malformed legacy fragments and continue with other blocks.
                }
            });

            const normalizedLinks = SNSLinksUtils.normalizeSnsLinks(collectedSnsLinks);

            if (normalizedLinks.length > 0) {
                // Merge sns_links into detectedLinks
                normalizedLinks.forEach((urlString) => {
                    const decodedLink = Patterns.decodeAmpersand(urlString);
                    const platform = this.detector.detectPlatform(decodedLink);
                    if (platform && platform !== "unknown") {
                        if (!detectedLinks[platform]) {
                            detectedLinks[platform] = [];
                        }
                        const linkObj = {
                            fullMatch: decodedLink,
                            id: null,
                            url: decodedLink,
                            index: 0,
                        };
                        const exists = detectedLinks[platform].some(
                            (item) =>
                                Patterns.decodeAmpersand(typeof item === "string" ? item : item.url) === decodedLink,
                        );
                        if (!exists) {
                            detectedLinks[platform].push(linkObj);
                        }
                    }
                });
            }
        }

        if (!shouldDisableEmbeds && !lightweightMode && this.options.enableEmbeds) {
            SnsEmbedRender.appendEmbedsForDetectedLinks(contentElement, detectedLinks);

            // Skip OGP rendering for preview tab (handled separately by updateOgpPreviewInTab)
            const isPreviewTab = snsPreviewManagerForm.isInsidePreviewBody(contentElement);
            // Board-level toggle (BoardConfigs.ogp_preview_show, see
            // post-detail-content.blade.php's data-ogp-preview-show attribute) —
            // boards with no real SNS links (e.g. guidance) never show an inline
            // OGP card for a stray content link. Default to shown when the
            // attribute is absent (non-detail contexts, e.g. the create form).
            const postDetailRoot = snsPreviewManagerForm.getClosestPostDetail(contentElement);
            const ogpPreviewAllowed = postDetailRoot?.dataset.ogpPreviewShow !== "0";
            if (!isPreviewTab && ogpPreviewAllowed) {
                // Process OGP for detail/modal views only
                this.ogpRenderer.processDetailContent(contentElement, {
                    maxUrls: 1,
                    renderMode: "full",
                });
            }
        }

        // Mark as processed
        contentElement.setAttribute("data-sns-processed", "true");
    }

    /**
     * Clear existing SNS and OGP previews to prevent duplication
     * @param {HTMLElement} contentElement - Content element to clear previews from
     */
    private clearExistingPreviews(contentElement: Element): void {
        try {
            // Remove existing SNS embeds
            const snsEmbeds = snsPreviewManagerForm.getSnsEmbeds(contentElement);
            snsEmbeds.forEach((embed) => embed.remove());

            // Remove existing SNS preview blocks (legacy generator)
            const snsPreviews = snsPreviewManagerForm.getYoutubePreviews(contentElement);
            snsPreviews.forEach((preview) => preview.remove());

            // Remove existing OGP previews
            const ogpPreviews = snsPreviewManagerForm.getOgpPreviews(contentElement);
            ogpPreviews.forEach((preview) => preview.remove());
        } catch (e) {
            console.error("Failed to clear existing previews", e);
        }
    }

    /**
     * Update placeholder text based on SNS processing result
     */
    private updatePlaceholderText(postElement: Element, text: string): void {
        const mediaArea = this.getCardMediaArea(postElement);
        const selector = `[data-role="${Roles.BOARD_MEDIA_PLACEHOLDER}"]`;
        const placeholderRoot = mediaArea
            ? snsPreviewManagerForm.queryScopedElement(mediaArea, selector)
            : snsPreviewManagerForm.queryScopedElement(postElement, selector);

        if (!(placeholderRoot instanceof HTMLElement)) return;

        // Genuinely no media at all (no images/video/SNS links) — render it the same
        // way SSR already does for other no-placeholder-needed cases
        // (data-placeholder-variant="empty", see board-placeholder.blade.php /
        // _placeholder.scss): fully blank, no visible box, instead of a "TEXT ONLY"
        // label that reads as broken/unexpected for what's actually a normal,
        // common case (most review posts have no photo attached).
        if (text === Placeholders.TEXT_ONLY) {
            placeholderRoot.setAttribute("data-placeholder-variant", "empty");
            placeholderRoot.replaceChildren();
            return;
        }

        const placeholderElement = snsPreviewManagerForm.getPlaceholderLoadingText(placeholderRoot);
        if (placeholderElement) {
            placeholderElement.textContent = text;
            placeholderElement.classList.remove("loading-placeholder");
            placeholderElement.classList.add("final-placeholder");
        }
    }

    /**
     * Get post content from various possible locations - Legacy compatible
     */
    private getPostContent(postElement: Element): string {
        // Legacy compatible content detection
        const hiddenContentEl = snsPreviewManagerForm.getHiddenContent(postElement);
        const visibleContentEl = snsPreviewManagerForm.getDisplayContent(postElement);
        const snsContentEl = snsPreviewManagerForm.getSnsContent(postElement);

        let content = "";
        if (hiddenContentEl) {
            content =
                hiddenContentEl.textContent ||
                (hiddenContentEl instanceof HTMLElement ? hiddenContentEl.innerText : "") ||
                "";
        } else if (visibleContentEl) {
            content =
                visibleContentEl.textContent ||
                (visibleContentEl instanceof HTMLElement ? visibleContentEl.innerText : "") ||
                "";
        }

        // Check for SNS iframe content (Facebook, etc.)
        if (snsContentEl) {
            const snsContent = snsContentEl.innerHTML || "";
            if (snsContent.trim()) {
                content += " " + snsContent;
            }
        }

        // Fallback to other selectors if no content found
        if (!content.trim()) {
            const contentSelectors = [
                SNSPreviewManagerHandler.displayContentSelector,
                ".board-content",
                ".card-text",
                ".board-card-content",
            ];
            for (const selector of contentSelectors) {
                const contentEl = snsPreviewManagerForm.queryScopedElement(postElement, selector);
                if (contentEl) {
                    return this.getElementContent(contentEl);
                }
            }

            // Final fallback
            return this.getElementContent(postElement);
        }

        return content;
    }

    /**
     * Add SNS icons to card
     */
    private addSNSIconsToCard(cardElement: Element, detectedLinks: DetectedLinksMap): void {
        if (!(cardElement instanceof HTMLElement)) return;
        SnsCardPreviewIconsRender.renderSnsIconsOnCard(cardElement, detectedLinks);
    }

    /**
     * Process OGP links for card view
     * Detects non-SNS URLs and renders OGP previews
     */
    private async processOgpForCard(cardElement: Element, content: string): Promise<void> {
        try {
            if (this.ogpRenderer?.isFetchSuppressed?.()) {
                return;
            }

            // Find media area first so we don't touch unrelated placeholders (e.g. linked-product thumb)
            const mediaArea = this.getCardMediaArea(cardElement);
            if (!(mediaArea instanceof HTMLElement)) {
                console.warn("processOgpForCard: No board media area found in card");
                this.updatePlaceholderText(cardElement, Placeholders.TEXT_ONLY);
                return;
            }

            // SSR already rendered an OGP preview (attached_images ogp_* flow) — skip CSR fetch
            if (snsPreviewManagerForm.getExistingPrimaryPreview(mediaArea)) {
                return;
            }

            const placeholder = snsPreviewManagerForm.getBoardMediaPlaceholder(mediaArea);

            const tilesGrid = snsPreviewManagerForm.getSnsPreviewTilesGrid(mediaArea);
            const hasTilesGrid = tilesGrid instanceof HTMLElement;

            // Mark placeholder as processing OGP
            if (placeholder) {
                placeholder.setAttribute("data-ogp-processing", "true");
            }

            // Detect URLs from content (anchor tags prioritized)
            const urls = this.ogpRenderer.detectUrls(content);

            if (!urls || urls.length === 0) {
                // No OGP URLs found
                if (placeholder) {
                    placeholder.removeAttribute("data-ogp-processing");
                }
                // Only show TEXT ONLY when there is no other media.
                const hasAnyMedia =
                    snsPreviewManagerForm.getSnsPreviewTilesGrid(mediaArea) ||
                    snsPreviewManagerForm.queryScopedElement(
                        mediaArea,
                        SNSPreviewManagerHandler.boardImagesGridSelector,
                    ) ||
                    snsPreviewManagerForm.getBoardVideoWrap(mediaArea) ||
                    snsPreviewManagerForm.queryScopedElement(mediaArea, `[data-role="${Roles.SNS_PREVIEW_TILE}"]`) ||
                    snsPreviewManagerForm.getOgpPreviewCard(mediaArea);
                if (!hasAnyMedia) {
                    this.updatePlaceholderText(cardElement, Placeholders.TEXT_ONLY);
                }
                return;
            }

            // Limit to first URL only for card view
            const urlsToProcess = urls.slice(0, 1);

            // If a tiles grid exists (mixed media), render OGP inside a tile.
            // Otherwise, keep legacy behavior (append into media area).
            const ogpPromises = urlsToProcess.map((url) => {
                if (hasTilesGrid && tilesGrid instanceof HTMLElement) {
                    if (tilesGrid.children.length >= 4) {
                        return Promise.resolve();
                    }
                    const tile = SnsCardPreviewMediaGridRender.appendOgpTileToIndexGrid(tilesGrid);
                    if (!(tile instanceof HTMLElement)) return Promise.resolve();
                    tilesGrid.setAttribute("data-count", String(tilesGrid.children.length));
                    return this.ogpRenderer.fetchAndRender(url, tile, "card");
                }
                return this.ogpRenderer.fetchAndRender(url, mediaArea, "card");
            });

            // Wait for all OGP fetches
            await Promise.all(ogpPromises);

            // Remove OGP URLs from post-content after successful rendering
            const postContent = snsPreviewManagerForm.getDisplayContent(cardElement);
            if (postContent instanceof HTMLElement) {
                this.ogpRenderer.removeTextUrls(postContent, urlsToProcess);
                this.removeAnchorTags(postContent, urlsToProcess);
            }

            // Update placeholder after successful OGP processing
            if (placeholder) {
                placeholder.removeAttribute("data-ogp-processing");
                // If OGP previews were added, remove placeholder and show preview
                const hasOgpPreviews = snsPreviewManagerForm.getOgpPreviewCard(mediaArea);
                if (hasOgpPreviews) {
                    // Remove the placeholder since we have OGP preview
                    placeholder.remove();
                }
            }

            // OGP preview takes precedence over any SSR placeholder ad in the media area.
            // When SSR assigned a PLACEHOLDER ad (sns_links was empty but content has a URL),
            // both the SSR ad slot and the OGP preview would be visible. Remove the ad slot.
            const hasOgpPreviewsNow = snsPreviewManagerForm.getOgpPreviewCard(mediaArea);
            if (hasOgpPreviewsNow) {
                snsPreviewManagerForm.getAdSlots(mediaArea).forEach((el) => el.remove());
            }

            if (hasTilesGrid && tilesGrid instanceof HTMLElement) {
                tilesGrid.setAttribute("data-count", String(tilesGrid.children.length));
            }
        } catch (e) {
            console.error("processOgpForCard failed", e);
            const mediaArea = this.getCardMediaArea(cardElement);
            const placeholder = mediaArea ? snsPreviewManagerForm.getBoardMediaPlaceholder(mediaArea) : null;
            if (placeholder) {
                placeholder.removeAttribute("data-ogp-processing");
            }
        }
    }

    /**
     * Remove anchor tags with specific URLs from element
     * @param {HTMLElement} element - Element to clean
     * @param {Array<string>} urls - URLs to remove
     */
    private removeAnchorTags(element: Element, urls: string[]): void {
        try {
            urls.forEach((url) => {
                // Find all anchor tags with this href
                const anchors = snsPreviewManagerForm.getAnchorElementsByHref(element, url);
                anchors.forEach((anchor) => {
                    // Check if anchor only contains the URL as text
                    const anchorText = anchor.textContent.trim();
                    if (anchorText === url || anchorText.startsWith(url)) {
                        // Remove the entire anchor tag
                        anchor.remove();
                    }
                });
            });
        } catch (e) {
            console.error("Failed to remove anchor tags", e);
        }
    }
}

export { SNSPreviewManagerHandler };
